import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { TILE_W, TILE_H } from '../config/constants';
import { isoToScreen, calculateDepthSort } from '../config/iso';
import { loadTexture, getTexture, getTextureDimensions, isTextureCached } from './SpriteCache';

/**
 * Entity Component System helpers.
 * Each entity has: container (parent), renderComponent, hitboxComponent, overlayComponent
 */

// Draw an isometric diamond tile
export function drawIsoTile(graphics, footW = 1, footH = 1, fillColor = 0x6366f1, strokeColor = 0xffffff, alpha = 1) {
  const topX = 0;
  const topY = 0;
  const rightX = footW * TILE_W / 2;
  const rightY = footW * TILE_H / 2;
  const bottomX = (footW - footH) * TILE_W / 2;
  const bottomY = (footW + footH) * TILE_H / 2;
  const leftX = -footH * TILE_W / 2;
  const leftY = footH * TILE_H / 2;
  
  const points = [topX, topY, rightX, rightY, bottomX, bottomY, leftX, leftY];
  graphics.poly(points);
  graphics.fill({ color: fillColor, alpha });
  graphics.stroke({ color: strokeColor, width: 2, alpha: Math.min(1, alpha + 0.2) });
}

/**
 * Create the render component (base tile + sprite if available).
 * @param {Object} asset - Asset definition with footprint, color, sprite
 * @param {Object} options - { isSelected, isHovered }
 * @returns {Container}
 */
export function createRenderComponent(asset, options = {}) {
  const { isSelected = false, isHovered = false } = options;
  
  const footW = asset?.footprint?.w || 1;
  const footH = asset?.footprint?.h || 1;
  const baseColor = asset?.color || 0x6366f1;
  
  const container = new Container();
  container.label = 'render';
  
  // Determine colors based on selection state
  const tileAlpha = isSelected ? 0.7 : isHovered ? 0.6 : 0.5;
  const tileFill = isSelected ? 0x3b82f6 : isHovered ? 0x60a5fa : baseColor;
  const tileStroke = isSelected ? 0xfbbf24 : 0xffffff;
  
  // Always draw base tile first
  const baseTile = new Graphics();
  drawIsoTile(baseTile, footW, footH, tileFill, tileStroke, tileAlpha);
  container.addChild(baseTile);
  
  // Add sprite if available
  if (asset?.sprite) {
    const addSprite = (texture, imgWidth) => {
      const spriteObj = new Sprite(texture);
      
      // Floor-contact anchor positioning
      const diamondBottomX = (footW - footH) * TILE_W / 2;
      const diamondBottomY = (footW + footH) * TILE_H / 2;
      
      spriteObj.anchor.set(0.5, 1.0);
      spriteObj.x = diamondBottomX;
      spriteObj.y = diamondBottomY;
      
      // Scale to match tile diagonal width
      const tileWidth = (footW + footH) * TILE_W / 2;
      const scale = (tileWidth / imgWidth) * 0.95;
      spriteObj.scale.set(scale);
      
      container.addChild(spriteObj);
    };
    
    // Use cached texture if available
    const cachedTexture = getTexture(asset.sprite);
    const cachedDims = getTextureDimensions(asset.sprite);
    
    if (cachedTexture && cachedDims) {
      addSprite(cachedTexture, cachedDims.width);
    } else {
      loadTexture(asset.sprite)
        .then((texture) => {
          const dims = getTextureDimensions(asset.sprite);
          if (dims) addSprite(texture, dims.width);
        })
        .catch(e => console.error('Failed to load sprite:', e));
    }
  }
  
  return container;
}

/**
 * Create an invisible hitbox for collision/selection.
 * Uses the full footprint area.
 * @param {Object} asset - Asset definition with footprint
 * @returns {Graphics}
 */
export function createHitboxComponent(asset) {
  const footW = asset?.footprint?.w || 1;
  const footH = asset?.footprint?.h || 1;
  
  const hitbox = new Graphics();
  hitbox.label = 'hitbox';
  
  // Draw invisible isometric diamond
  const topX = 0;
  const topY = 0;
  const rightX = footW * TILE_W / 2;
  const rightY = footW * TILE_H / 2;
  const bottomX = (footW - footH) * TILE_W / 2;
  const bottomY = (footW + footH) * TILE_H / 2;
  const leftX = -footH * TILE_W / 2;
  const leftY = footH * TILE_H / 2;
  
  const points = [topX, topY, rightX, rightY, bottomX, bottomY, leftX, leftY];
  hitbox.poly(points);
  hitbox.fill({ color: 0x000000, alpha: 0.001 }); // Nearly invisible but hittable
  
  hitbox.eventMode = 'static';
  hitbox.cursor = 'pointer';
  
  return hitbox;
}

/**
 * Create an overlay component for status indicators, labels, etc.
 * @param {Object} entity - Entity data
 * @param {Object} asset - Asset definition
 * @param {Object} machineStatus - Optional machine status data
 * @returns {Container}
 */
export function createOverlayComponent(entity, asset, machineStatus = null) {
  const footW = asset?.footprint?.w || 1;
  const footH = asset?.footprint?.h || 1;
  
  const container = new Container();
  container.label = 'overlay';
  
  // Position at top-center of tile
  const centerX = (footW - footH) * TILE_W / 4;
  const centerY = -10; // Slightly above the tile
  
  // Add name label if entity has one
  if (entity.meta?.name || asset?.name) {
    const label = new Text({
      text: entity.meta?.name || asset?.name || '',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
        dropShadow: { blur: 2, alpha: 0.5, distance: 1 },
      }
    });
    label.anchor.set(0.5, 1);
    label.x = centerX;
    label.y = centerY;
    container.addChild(label);
  }
  
  // Add status indicator if machine data available
  if (machineStatus) {
    // Machine state payload exposes `execution` (ACTIVE/READY/…), not `state`.
    const statusColor = getStatusColor(machineStatus.execution);
    const indicator = new Graphics();
    indicator.circle(centerX, centerY - 20, 6);
    indicator.fill(statusColor);
    indicator.stroke({ color: 0xffffff, width: 1 });
    container.addChild(indicator);
  }
  
  return container;
}

function getStatusColor(execution) {
  switch (execution?.toUpperCase()) {
    case 'ACTIVE':  return 0x22c55e; // green — cutting (engine `execution` vocabulary)
    case 'READY':   return 0xfbbf24; // yellow — idle/ready
    // legacy/game vocabulary, kept for back-compat
    case 'RUNNING': return 0x22c55e;
    case 'IDLE':    return 0xfbbf24;
    case 'DOWN':    return 0xef4444; // red
    case 'SETUP':   return 0x3b82f6; // blue
    default:        return 0x6b7280; // gray
  }
}

/**
 * Create a complete entity container with all components.
 * @param {Object} entity - Entity data from store
 * @param {Object} asset - Asset definition
 * @param {Object} options - { isSelected, isHovered, machineStatus, onClick, onDragStart, onDragMove, onDragEnd }
 * @returns {Container}
 */
export function createEntityContainer(entity, asset, options = {}) {
  const { 
    isSelected = false, 
    isHovered = false, 
    machineStatus = null, 
    onClick = null,
    onDragStart = null,
    onDragMove = null,
    onDragEnd = null,
  } = options;
  const { tx, ty, id } = entity;
  
  const pos = isoToScreen(tx, ty);
  
  // Main container
  const container = new Container();
  container.label = `entity-${id}`;
  container.x = pos.x;
  container.y = pos.y;
  container.zIndex = calculateDepthSort(tx, ty);
  
  // Store entity data for drag operations
  container.entityId = id;
  container.entityTx = tx;
  container.entityTy = ty;
  
  // Add render component
  const renderComp = createRenderComponent(asset, { isSelected, isHovered });
  container.addChild(renderComp);
  
  // Add hitbox component
  const hitboxComp = createHitboxComponent(asset);
  
  // Drag state
  let isDragging = false;
  let dragData = null;
  
  hitboxComp.on('pointerdown', (e) => {
    e.stopPropagation();
    
    if (onClick) {
      onClick(id);
    }
    
    // Start drag if selected
    if (isSelected && onDragStart) {
      isDragging = true;
      dragData = e;
      container.alpha = 0.7;
      onDragStart(id, e);
    }
  });
  
  hitboxComp.on('globalpointermove', (e) => {
    if (isDragging && onDragMove) {
      onDragMove(id, e);
    }
  });
  
  hitboxComp.on('pointerup', (e) => {
    if (isDragging && onDragEnd) {
      isDragging = false;
      container.alpha = 1;
      onDragEnd(id, e);
    }
    dragData = null;
  });
  
  hitboxComp.on('pointerupoutside', (e) => {
    if (isDragging && onDragEnd) {
      isDragging = false;
      container.alpha = 1;
      onDragEnd(id, e);
    }
    dragData = null;
  });
  
  container.addChild(hitboxComp);
  
  // Add overlay component
  const overlayComp = createOverlayComponent(entity, asset, machineStatus);
  container.addChild(overlayComp);
  
  return container;
}

/**
 * Update just the render component's selection state without recreating.
 * @param {Container} entityContainer - The entity's container
 * @param {Object} asset - Asset definition
 * @param {Object} options - { isSelected, isHovered }
 */
export function updateRenderSelection(entityContainer, asset, options = {}) {
  const { isSelected = false, isHovered = false } = options;
  
  const renderComp = entityContainer.getChildByLabel('render');
  if (!renderComp) return;
  
  const baseTile = renderComp.children[0];
  if (!baseTile || !baseTile.clear) return;
  
  const footW = asset?.footprint?.w || 1;
  const footH = asset?.footprint?.h || 1;
  const baseColor = asset?.color || 0x6366f1;
  
  const tileAlpha = isSelected ? 0.7 : isHovered ? 0.6 : 0.5;
  const tileFill = isSelected ? 0x3b82f6 : isHovered ? 0x60a5fa : baseColor;
  const tileStroke = isSelected ? 0xfbbf24 : 0xffffff;
  
  baseTile.clear();
  drawIsoTile(baseTile, footW, footH, tileFill, tileStroke, tileAlpha);
}

/**
 * Update the overlay component (e.g., when machine status changes).
 * @param {Container} entityContainer 
 * @param {Object} entity 
 * @param {Object} asset 
 * @param {Object} machineStatus 
 */
export function updateOverlayComponent(entityContainer, entity, asset, machineStatus) {
  // Remove old overlay
  const oldOverlay = entityContainer.getChildByLabel('overlay');
  if (oldOverlay) {
    entityContainer.removeChild(oldOverlay);
    oldOverlay.destroy();
  }
  
  // Create new overlay
  const newOverlay = createOverlayComponent(entity, asset, machineStatus);
  entityContainer.addChild(newOverlay);
}
